import {
  CheckCircle2,
  Send,
  Pencil,
  Trash2,
  X,
  FileCheck,
} from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";

// ─── Types ───────────────────────────────────────────────────────────────────

interface PreviewCard {
  title: string;
  rows: Array<{ label: string; value: string }>;
}

interface DraftSuccessViewProps {
  entityType: "challenge" | "event";
  variant?: "created" | "updated";
  name: string;
  photoUrl?: string | null;
  cards: PreviewCard[];
  description?: string;
  onPublish: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium text-foreground">
        {value}
      </span>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DraftSuccessView({
  entityType,
  variant = "created",
  name,
  photoUrl,
  cards,
  description,
  onPublish,
  onEdit,
  onDelete,
  onClose,
}: DraftSuccessViewProps) {
  const { t } = useLocale();
  const isUpdated = variant === "updated";
  const headerTitle = isUpdated
    ? (entityType === "challenge" ? t("shared.draftSuccess.challengeUpdated") : t("shared.draftSuccess.eventUpdated"))
    : t("shared.draftSuccess.draftCreated");
  const bannerTitle = isUpdated
    ? (entityType === "challenge" ? t("shared.draftSuccess.challengeUpdatedBanner") : t("shared.draftSuccess.eventUpdatedBanner"))
    : (entityType === "challenge" ? t("shared.draftSuccess.challengeDraftBanner") : t("shared.draftSuccess.eventDraftBanner"));

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 className="text-lg font-semibold text-foreground">
          {headerTitle}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 hover:bg-muted"
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-6">
        {/* Success banner */}
        <div className="flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle2 className="h-6 w-6 text-success" />
          </div>
          <h3 className="mt-3 text-base font-semibold text-foreground">
            {bannerTitle}
          </h3>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            {t("shared.draftSuccess.verifyDetails")}
          </p>
        </div>

        {/* Photo */}
        {photoUrl && (
          <div className="overflow-hidden rounded-lg">
            <img
              src={photoUrl}
              alt={name}
              className="h-40 w-full object-cover"
            />
          </div>
        )}

        {/* Preview cards — mirrors mobile PreviewRow card pattern */}
        {cards.map((card) => (
          <div
            key={card.title}
            className="rounded-lg border border-border bg-muted/20 p-4"
          >
            <h4 className="mb-2 text-sm font-semibold text-foreground">
              {card.title}
            </h4>
            {card.rows.map((row) => (
              <PreviewRow key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
        ))}

        {/* Description */}
        {description && (
          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <h4 className="mb-2 text-sm font-semibold text-foreground">
              {t("shared.draftSuccess.descriptionLabel")}
            </h4>
            <p className="text-sm leading-relaxed text-foreground">
              {description}
            </p>
          </div>
        )}

        {/* Hint */}
        <p className="text-center text-xs text-muted-foreground">
          {t("shared.draftSuccess.draftsHint")}
        </p>
      </div>

      {/* Action Buttons — Row 1: Publish, Row 2: Edit · Keep as Draft · Delete */}
      <div className="border-t border-border px-6 py-4">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onPublish}
            className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Send className="h-4 w-4" />
            {t("shared.draftSuccess.publishNow")}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md border border-border text-sm font-medium text-foreground hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5" />
              {t("common.edit")}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md border border-border text-sm font-medium text-muted-foreground hover:bg-muted"
            >
              <FileCheck className="h-3.5 w-3.5" />
              {t("shared.draftSuccess.keepAsDraft")}
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-destructive/30 px-4 text-sm font-medium text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
